import {api} from '../../api.js';
import {apiUrl} from '../../config.js';
import {AuthSnapshot} from '../../auth.js';
import {escapeHtml, renderLayout} from '../../layout.js';

type IntegrationStatus = 'platform_off' | 'disabled' | 'pending' | 'ready';

type IntegrationCard = {
  integrationId: 'qichacha-mcp' | 'pkulaw-mcp';
  title: string;
  enabled: boolean;
  configured: boolean;
  platformAvailable: boolean;
  status: IntegrationStatus;
  maskedSecret: string | null;
  maskedAppKey?: string | null;
  mcpUrl?: string;
  baseUrl?: string;
  endpoints?: Record<string, string>;
  endpointLabels: Record<string, string>;
};

type IntegrationsSnapshot = {
  platform: {qichachaMcpEnabled: boolean; qichachaKycConfigured?: boolean; pkulawMcpEnabled: boolean};
  configFolderId: string;
  qichachaMcp: IntegrationCard;
  pkulawMcp: IntegrationCard;
};

const PKU_ENDPOINT_ORDER = [
  'lawSearch',
  'law',
  'statute',
  'caseSearch',
  'case',
  'lawRecognition',
  'citationValidator',
  'documentLink',
  'caseNumberRecognition',
  'lawAggregate',
] as const;

/**
 * 状态标签文案。
 * @param status 卡片状态
 */
function statusLabel(status: IntegrationStatus): string {
  if (status === 'platform_off') return '平台已关闭';
  if (status === 'disabled') return '未启用';
  if (status === 'pending') return '待填写凭证';
  return '已就绪';
}

/**
 * 渲染外部接入页。
 * @param root 挂载根节点
 * @param auth 登录快照
 */
export async function renderExternalIntegrationsPage(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
  renderLayout(root, auth, '<div class="integrations-page"><section class="integrations-loading">正在加载外部接入…</section></div>');
  const host = root.querySelector<HTMLElement>('.integrations-page');
  if (!host) return;

  let snapshot: IntegrationsSnapshot | null = null;

  const load = async (): Promise<IntegrationsSnapshot> => {
    const payload = await api.get<{snapshot: IntegrationsSnapshot}>(apiUrl('/api/user/integrations'));
    snapshot = payload.snapshot;
    return snapshot;
  };

  const renderPkulawAdvanced = (card: IntegrationCard): string => {
    const endpoints = card.endpoints ?? {};
    return PKU_ENDPOINT_ORDER.map(key => {
      const label = card.endpointLabels[key] ?? key;
      const value = endpoints[key] ?? '';
      return `<label><span>${escapeHtml(label)}</span><input class="integrations-input" data-pku-endpoint="${escapeHtml(key)}" type="url" value="${escapeHtml(value)}" ${card.platformAvailable ? '' : 'readonly'}></label>`;
    }).join('');
  };

  const draw = (): void => {
    if (!snapshot) return;
    const qcc = snapshot.qichachaMcp;
    const pku = snapshot.pkulawMcp;
    host!.innerHTML = `<div class="integrations-page-inner">
      ${!pku.platformAvailable && !qcc.platformAvailable ? '<section class="integrations-banner is-warning"><strong>平台暂未开放外部接入</strong><span>请联系管理员在后台开启企查查或北大法宝。</span></section>' : ''}
      <section class="integrations-card integrations-card-readonly" data-integration="qichacha">
        <div class="integrations-card-head">
          <div><h2>企查查</h2><p class="integrations-card-note">由平台统一接入，用于合同预览中的企业性质与关联关系识别。</p></div>
          <span class="integrations-pill is-${escapeHtml(qcc.status)}">${escapeHtml(statusLabel(qcc.status))}</span>
        </div>
        <div class="integrations-managed-row">
          <span class="integrations-managed-icon" aria-hidden="true">⌁</span>
          <div><strong>平台统一管理</strong><span>${qcc.configured ? '当前可用于合同预览' : qcc.platformAvailable ? '后台尚未完成企查查配置' : '平台暂未开放企查查能力'}</span></div>
          <span class="integrations-managed-lock">用户端无需填写凭证</span>
        </div>
      </section>
      <section class="integrations-card" data-integration="pkulaw">
        <div class="integrations-card-head">
          <h2>${escapeHtml(pku.title)}</h2>
          <span class="integrations-pill is-${escapeHtml(pku.status)}">${escapeHtml(statusLabel(pku.status))}</span>
        </div>
        <div class="integrations-inline-row">
          <label class="integrations-inline-field"><span>法律法规检索地址</span><input class="integrations-input" id="pkulawBaseUrl" type="url" value="${escapeHtml(pku.baseUrl ?? '')}" ${pku.platformAvailable ? '' : 'readonly'}></label>
          <label class="integrations-inline-field"><span>访问 Token</span><input class="integrations-input" id="pkulawToken" type="password" autocomplete="off" placeholder="${escapeHtml(pku.maskedSecret ? `已保存 ${pku.maskedSecret}` : '请输入 Token')}" ${pku.platformAvailable ? '' : 'readonly'}></label>
        </div>
        <details class="integrations-advanced"><summary>高级：10 个 MCP 服务地址</summary><div class="integrations-advanced-grid">${renderPkulawAdvanced(pku)}</div></details>
        <div class="integrations-actions">
          <span class="integrations-message" id="pkulawMessage" role="status"></span>
          <button type="button" class="integrations-button" id="testPkulaw" ${pku.platformAvailable ? '' : 'disabled'}>测试检索</button>
          <button type="button" class="integrations-button" id="inspectPkulaw" ${pku.platformAvailable ? '' : 'disabled'}>检测全部服务</button>
          <button type="button" class="integrations-button" id="clearPkulaw" ${pku.platformAvailable ? '' : 'disabled'}>清除 Token</button>
          <button type="button" class="integrations-button is-primary" id="savePkulaw" ${pku.platformAvailable ? '' : 'disabled'}>保存</button>
        </div>
        <div class="integrations-inspection" id="pkulawInspection" hidden></div>
      </section>
    </div>`;

    const pkulawBody = (): Record<string, unknown> => {
      const endpoints: Record<string, string> = {};
      host.querySelectorAll<HTMLInputElement>('[data-pku-endpoint]').forEach(input => {
        const key = input.dataset.pkuEndpoint ?? '';
        if (key) endpoints[key] = input.value.trim();
      });
      return {
        enabled: true,
        baseUrl: host.querySelector<HTMLInputElement>('#pkulawBaseUrl')?.value ?? '',
        token: host.querySelector<HTMLInputElement>('#pkulawToken')?.value ?? '',
        endpoints,
      };
    };

    const setMessage = (id: string, text: string, isError = false): void => {
      const el = host.querySelector<HTMLElement>(id);
      if (!el) return;
      el.textContent = text;
      el.classList.toggle('is-error', isError);
    };

    host.querySelector('#savePkulaw')?.addEventListener('click', async () => {
      try {
        setMessage('#pkulawMessage', '正在保存…');
        const payload = await api.patch<{snapshot: IntegrationsSnapshot}>(apiUrl('/api/user/integrations/pkulaw-mcp'), pkulawBody());
        snapshot = payload.snapshot;
        draw();
        setMessage('#pkulawMessage', '已保存');
      } catch (error) {
        setMessage('#pkulawMessage', error instanceof Error ? error.message : String(error), true);
      }
    });

    host.querySelector('#testPkulaw')?.addEventListener('click', async () => {
      try {
        setMessage('#pkulawMessage', '正在测试…');
        const result = await api.post<{ok: boolean; message: string}>(apiUrl('/api/user/integrations/pkulaw-mcp/test'), pkulawBody());
        setMessage('#pkulawMessage', result.message, !result.ok);
      } catch (error) {
        setMessage('#pkulawMessage', error instanceof Error ? error.message : String(error), true);
      }
    });

    host.querySelector('#inspectPkulaw')?.addEventListener('click', async () => {
      const inspection = host.querySelector<HTMLElement>('#pkulawInspection');
      try {
        setMessage('#pkulawMessage', '正在检测全部服务…');
        const result = await api.post<{ok: boolean; services: Array<{name: string; ok: boolean; message: string}>}>(
          apiUrl('/api/user/integrations/pkulaw-mcp/test-all'),
          pkulawBody(),
        );
        if (inspection) {
          inspection.hidden = false;
          inspection.innerHTML = result.services.map(item => `<div class="integrations-inspection-row"><strong>${escapeHtml(item.name)}</strong><span class="${item.ok ? 'is-ok' : 'is-fail'}">${item.ok ? '通过' : '失败'}</span>${item.ok ? '' : `<small>${escapeHtml(item.message)}</small>`}</div>`).join('');
        }
        setMessage('#pkulawMessage', result.ok ? '全部服务检测通过' : '检测完成，存在失败项', !result.ok);
      } catch (error) {
        setMessage('#pkulawMessage', error instanceof Error ? error.message : String(error), true);
      }
    });

    host.querySelector('#clearPkulaw')?.addEventListener('click', async () => {
      if (!window.confirm('确定清除已保存的北大法宝 Token 吗？')) return;
      try {
        const payload = await api.patch<{snapshot: IntegrationsSnapshot}>(apiUrl('/api/user/integrations/pkulaw-mcp'), {...pkulawBody(), enabled: false, clearToken: true, token: ''});
        snapshot = payload.snapshot;
        draw();
        setMessage('#pkulawMessage', '已清除 Token');
      } catch (error) {
        setMessage('#pkulawMessage', error instanceof Error ? error.message : String(error), true);
      }
    });
  };

  try {
    await load();
    draw();
  } catch (error) {
    host.innerHTML = `<section class="integrations-error"><h2>无法加载外部接入</h2><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></section>`;
  }
}
